/**
 * OCEAN — industry problem statements, searchable and structured for academics
 * (PRD "OCEAN", Purvi Shah, Draft 1.0, 2026-08-14; prototype
 * claude.ai/artifact/BQA7Q41z44GZ15rmAHW8YP). Rendered by App.tsx's 'ocean'
 * view for accounts the "ocean" feature access lets in; the API is
 * Backend/ocean/routes.py.
 *
 * State lives here; the views are Discovery (search page), Cards (base card +
 * drawer) and Sheets (collections, invite, toast). Saved cards and their
 * collections are listed on the Profile page, Saved → OCEAN (SavedView); a
 * shared collection's link opens SharedCollectionView here; a COSMOS
 * technology opens App's Cosmos technology card (onOpenTech).
 *
 * Saving: the bookmark (on a result row or a card) opens the collection
 * picker: the member's collections, a new one by name ("Create & save"), and
 * for a saved card, move or "Remove from saved". The toast after a save offers
 * "View in Profile".
 *
 * Search: filters apply at once; the keyword applies on Search / Enter, which
 * also records the search in the history. Problems and companies page
 * separately, 10 per page on the web and 5 on a phone (PRD "Pagination").
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_FILTERS, lastCollectionId, oc, rememberCollection } from './api';
import type { CompanyMini, FacetCounts, FilterState, HistoryEntry, Kind, Meta, Page, ProblemItem, SavedState, Scope, SearchMeta,
  Suggestions } from './api';
import { BaseCard, Drawer } from './Cards';
import type { Crumb } from './Cards';
import { OceanContext } from './context';
import type { CardRef, OceanCtx } from './context';
import { FiltersBody, FiltersDrawer, FiltersPane, HistoryDrawer, HistoryPane, ResultsPane, SearchHeader } from './Discovery';
import { COLLECTIONS_ENABLED, focusSavedCollection, takeDeepLink } from './flags';
import { SharedCollectionView } from './SavedView';
import { CollectionSheet, InviteSheet, Toast } from './Sheets';
import type { ToastState } from './Sheets';
import { useOceanStyles } from './styles';
import { ocTrack } from './track';
import { useIsMobile } from './ui';
import { useTechCardStyles } from '../techcard/styles';

const short = (s: string, n = 24) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const anyFilter = (f: FilterState) => !!(f.q || f.countries.length || f.industries.length || f.goals.length || f.tags.length);

interface Props {
  /** Opens the app's Profile page on a tab ('ocean': the saved OCEAN cards). */
  onOpenProfile?: (tab?: string) => void;
  /** Opens the Cosmos technology card (App's handleReadMore); without it, a
   *  technology opens in OCEAN's drawer (the localhost harness). */
  onOpenTech?: (id: string, relevance?: number) => void;
}

const OceanApp: React.FC<Props> = ({ onOpenProfile, onOpenTech }) => {
  useOceanStyles();
  useTechCardStyles();   // the Cosmos share menu on every card
  const isMobile = useIsMobile();
  const perPage = isMobile ? 5 : 10;

  const [meta, setMeta] = useState<Meta | null>(null);
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [problems, setProblems] = useState<Page<ProblemItem> | null>(null);
  const [companies, setCompanies] = useState<Page<CompanyMini> | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | undefined>();
  const [facets, setFacets] = useState<FacetCounts | null>(null);
  const [searchMeta, setSearchMeta] = useState<SearchMeta | null>(null);
  const anywayRef = useRef(false);
  const [scope, setScopeState] = useState<Scope | null>(() => {
    try { const v = localStorage.getItem('ocean_card_scope'); return v === 'industry' || v === 'all' ? v : null; } catch { return null; }
  });
  const setScope = useCallback((s: Scope) => {
    setScopeState(s);
    try { localStorage.setItem('ocean_card_scope', s); } catch { /* private window */ }
  }, []);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingC, setLoadingC] = useState(true);
  const [error, setError] = useState('');
  // A search in flight (new words or filters): the results pane shows Searching…, never the previous list.
  const [searching, setSearching] = useState(true);
  // Cosmos's panes: collapsible on the desktop, drawers on narrow screens.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [filtersDrawer, setFiltersDrawer] = useState(false);
  const [historyDrawer, setHistoryDrawer] = useState(false);
  const [base, setBase] = useState<CardRef | null>(null);
  const [stack, setStack] = useState<CardRef[]>([]);
  const [saved, setSaved] = useState<SavedState>({ collections: [], items: [] });
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [sheet, setSheet] = useState<{ kind: Kind; id: string } | 'manage' | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  /** A shared collection opened from its link (its share token). */
  const [sharedToken, setSharedToken] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const problemsRef = useRef<HTMLElement>(null);
  const companiesRef = useRef<HTMLElement>(null);
  const gen = useRef(0);
  const secGen = useRef({ problems: 0, companies: 0 });
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  const flash = useCallback((msg: string, action?: ToastState['action'], action2?: ToastState['action']) =>
    setToast({ msg, action, action2 }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  // ---------------------------------------------------------------- data
  const reloadSaved = useCallback(async () => {
    const r = await oc.saved();
    if (r.ok && r.data) setSaved({ collections: r.data.collections, items: r.data.items });
  }, []);
  const reloadHistory = useCallback(async () => {
    const r = await oc.history();
    if (r.ok && r.data) setHistory(r.data.history);
  }, []);

  const runSearch = useCallback(async (f: FilterState, record = false, anyway = false) => {
    const g = ++gen.current;
    anywayRef.current = anyway;
    setLoadingP(true); setLoadingC(true); setSearching(true); setError('');
    const res = await oc.search(f, 'all', 1, 1, perPage, undefined, { anyway });
    if (g !== gen.current || res.status === -1) return;
    setLoadingP(false); setLoadingC(false); setSearching(false);
    if (!res.ok || !res.data) { setError(res.error); return; }
    setProblems(res.data.problems || null);
    setCompanies(res.data.companies || null);
    setSuggestions(res.data.suggestions);
    setFacets(res.data.facets || null);
    setSearchMeta(res.data.search || null);
    if (record && anyFilter(f) && res.data.search?.route !== 'nonsense') {
      const hits = res.data.problems?.total ?? 0;
      ocTrack('ocean_search', { q_len: f.q.length, goals: f.goals, countries: f.countries.length,
                                industries: f.industries.length, tags: f.tags.length, hits });
      const h = await oc.addHistory(f, hits);
      if (h.ok) reloadHistory();
    }
  }, [perPage, reloadHistory]);

  const loadSection = useCallback(async (section: 'problems' | 'companies', page: number) => {
    const g = gen.current;
    const s = ++secGen.current[section];
    (section === 'problems' ? setLoadingP : setLoadingC)(true);
    const res = await oc.search(appliedRef.current, section, section === 'problems' ? page : 1,
                                section === 'companies' ? page : 1, perPage, undefined, { anyway: anywayRef.current });
    if (g !== gen.current || s !== secGen.current[section] || res.status === -1) return;
    (section === 'problems' ? setLoadingP : setLoadingC)(false);
    if (!res.ok || !res.data) { setError(res.error); return; }
    if (section === 'problems' && res.data.problems) setProblems(res.data.problems);
    if (section === 'companies' && res.data.companies) setCompanies(res.data.companies);
    const el = (section === 'problems' ? problemsRef : companiesRef).current;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [perPage]);

  const applyFilters = useCallback((next: FilterState, record = false) => {
    if (next.q !== appliedRef.current.q) setQuery(next.q);
    setApplied(next);
    appliedRef.current = next;
    runSearch(next, record);
  }, [runSearch]);

  useEffect(() => {
    ocTrack('ocean_view');
    oc.meta().then(r => { if (r.ok && r.data) setMeta(r.data); else if (r.error) setError(r.error); });
    reloadSaved();
    reloadHistory();
    const dl = takeDeepLink();
    if (dl?.kind === 'problem') setBase({ type: 'problem', id: dl.id });
    else if (dl?.kind === 'company') setBase({ type: 'company', id: dl.id });
    else if (dl?.kind === 'solution') {
      if (onOpenTech) onOpenTech(dl.id);
      else setStack([{ type: 'solution', id: dl.id }]);
    } else if (dl?.kind === 'collection') setSharedToken(dl.id);
    if (dl?.kind === 'collection') ocTrack('ocean_collection_open', { from: 'share_link' });
    else if (dl) ocTrack('ocean_open', { kind: dl.kind, from: dl.share ? 'share_link' : 'profile' });
    // A tracked share link (?s=): the open counts on the admin Shares page.
    if (dl?.share) oc.shareOpen(dl.share, `/?view=ocean&ocean=${dl.kind}:${dl.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSaved, reloadHistory]);

  // First load, and again when the page size changes (phone <-> web).
  useEffect(() => { runSearch(appliedRef.current); }, [runSearch]);

  // ---------------------------------------------------------------- saved
  const savedIndex = useMemo(() => {
    const m = new Map<string, number>();
    saved.items.forEach(i => m.set(`${i.kind}:${i.id}`, i.collection_id));
    return m;
  }, [saved.items]);
  const colName = useCallback((id: number | null | undefined) => saved.collections.find(c => c.id === id)?.name ?? null,
                              [saved.collections]);

  /** "View in Profile" / "View all" / a collection in the rail: Profile → Saved → OCEAN (on that collection). */
  const openSavedView = useCallback((collectionId?: number) => {
    setBase(null); setStack([]); setSheet(null); setSharedToken(null);
    focusSavedCollection(collectionId ?? null);
    onOpenProfile?.('ocean');
  }, [onOpenProfile]);
  const profileAction = useMemo(() => (onOpenProfile ? { label: 'View in Profile', run: () => openSavedView() } : undefined),
                                [onOpenProfile, openSavedView]);

  const toggleSave = useCallback(async (kind: Kind, id: string) => {
    const cid = savedIndex.get(`${kind}:${id}`);
    if (cid) {
      setSaved(s => ({ ...s, items: s.items.filter(i => !(i.kind === kind && i.id === id)) }));
      const r = await oc.unsave(kind, id);
      flash(r.ok ? `Removed from ${(COLLECTIONS_ENABLED && colName(cid)) || 'your saved cards'}.` : r.error,
            r.ok ? { label: 'Undo', run: () => { oc.save(kind, id, cid).then(() => reloadSaved()); } } : undefined);
      ocTrack('ocean_unsave', { kind });
    } else {
      // One click: the collection last used (still there), else the default; "Change" opens the picker.
      const last = lastCollectionId();
      const target = (COLLECTIONS_ENABLED && saved.collections.find(c => c.id === last)) || saved.collections.find(c => c.is_default);
      if (target) {
        setSaved(s => ({ ...s, items: [{ kind, id, collection_id: target.id, title: '', meta: '', available: true, saved_at: null }, ...s.items] }));
      }
      const r = await oc.save(kind, id, target && !target.is_default ? target.id : undefined);
      if (r.ok) {
        const change = COLLECTIONS_ENABLED
          ? { label: 'Change', run: () => { ocTrack('ocean_save_change', { kind }); setSheet({ kind, id }); } } : undefined;
        flash(`Saved to ${r.data?.collection || target?.name || 'your saved cards'}.`, change || profileAction,
              change ? profileAction : undefined);
        ocTrack('ocean_save', { kind, collection: target && !target.is_default ? 'last_used' : 'default' });
      } else flash(r.error);
    }
    reloadSaved();
  }, [savedIndex, saved.collections, colName, flash, profileAction, reloadSaved]);

  // ---------------------------------------------------------------- navigation
  const ctx: OceanCtx = useMemo(() => ({
    savedOf: (kind, id) => {
      const cid = savedIndex.get(`${kind}:${id}`);
      return cid ? { collection_id: cid, collection: colName(cid) } : null;
    },
    toggleSave: (kind, id) => { toggleSave(kind, id); },
    chooseCollection: (kind, id, from) => {
      if (!COLLECTIONS_ENABLED) { toggleSave(kind, id); return; }
      ocTrack('ocean_picker_open', { kind, saved: savedIndex.has(`${kind}:${id}`), from: from || 'card' });
      setSheet({ kind, id });
    },
    openTech: (id, relevance, label, from = 'base') => {
      ocTrack('ocean_open', { kind: 'solution', from });
      if (onOpenTech) { onOpenTech(id, relevance); return; }
      const ref: CardRef = { type: 'solution', id, label };
      if (from === 'drawer') setStack(s => [...s, ref].slice(-12)); else setStack([ref]);
    },
    toast: msg => flash(msg),
    openProblem: (id, from, c) => { setBase({ type: 'problem', id, ctx: c?.q ? { q: c.q } : undefined }); setStack([]); ocTrack('ocean_open', { kind: 'problem', from }); },
    openCompanyBase: (id, c, from) => { setBase({ type: 'company', id, ctx: c }); setStack([]); ocTrack('ocean_open', { kind: 'company', from }); },
    openFromBase: ref => { setStack([ref]); ocTrack('ocean_open', { kind: ref.type, from: 'base' }); },
    pushDrawer: ref => { setStack(s => [...s, ref].slice(-12)); ocTrack('ocean_open', { kind: ref.type, from: 'drawer' }); },
    floorLabel: meta?.dataset.crawl_date
      ? new Date(`${meta.dataset.crawl_date}T00:00:00`).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) : 'June 2026',
    isMobile,
    scope,
    setScope,
  }), [savedIndex, colName, toggleSave, onOpenTech, flash, meta, isMobile, scope, setScope]);

  const top = stack[stack.length - 1] || null;
  const crumbs: Crumb[] = [];
  if (base) crumbs.push({ label: base.type === 'problem' ? 'Problem' : 'Company', onClick: () => setStack([]) });
  stack.forEach((r, i) => crumbs.push({
    label: r.label ? short(r.label) : (r.type === 'solution' ? 'Solution' : 'Company'),
    onClick: () => setStack(s => s.slice(0, i + 1)),
  }));
  const backLabel = stack.length > 1
    ? `Back to ${stack[stack.length - 2].label ? short(stack[stack.length - 2].label!, 30) : 'the previous card'}`
    : base ? `Back to the ${base.type} card` : 'Close';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The Cosmos technology card (opened over a card) and a share menu
      // close on their own Escape; the OCEAN card under them stays.
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('.tcx-modal, .tcx.tcx-menu')) return;
      if (sheet) setSheet(null);
      else if (inviteOpen) setInviteOpen(false);
      else if (stack.length) setStack([]);
      else if (base) setBase(null);
      else if (sharedToken) setSharedToken(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet, inviteOpen, stack.length, base, sharedToken]);

  // ---------------------------------------------------------------- sheet actions
  const sheetTarget = sheet && sheet !== 'manage' ? sheet : null;
  const pickCollection = async (collectionId: number, how: 'picked' | 'new' = 'picked') => {
    if (!sheetTarget) { setSheet(null); openSavedView(collectionId); return; }
    const was = savedIndex.get(`${sheetTarget.kind}:${sheetTarget.id}`);
    const r = await oc.save(sheetTarget.kind, sheetTarget.id, collectionId);
    setSheet(null);
    const name = r.data?.collection || colName(collectionId) || 'the collection';
    flash(r.ok ? `${was ? 'Moved' : 'Saved'} to ${name}.` : r.error, r.ok ? profileAction : undefined);
    if (r.ok) {
      rememberCollection(collectionId);
      ocTrack(was ? 'ocean_move' : 'ocean_save', { kind: sheetTarget.kind, collection: how, from: 'picker' });
    }
    reloadSaved();
  };
  const createCollection = async (name: string): Promise<string> => {
    const r = await oc.createCollection(name);
    if (!r.ok || !r.data) return r.error;
    ocTrack('ocean_collection_create', { from: sheetTarget ? 'picker' : 'ocean' });
    if (sheetTarget) await pickCollection(r.data.collection.id, 'new');
    else { flash(`Collection “${r.data.collection.name}” created.`); reloadSaved(); }
    return '';
  };
  const renameCollection = async (id: number, name: string): Promise<string> => {
    const r = await oc.renameCollection(id, name);
    if (!r.ok) return r.error;
    reloadSaved();
    return '';
  };
  const deleteCollection = async (id: number) => {
    const name = colName(id);
    const r = await oc.deleteCollection(id);
    flash(r.ok ? `“${name}” deleted — its cards moved to ${saved.collections.find(c => c.is_default)?.name || 'Saved'}.` : r.error);
    reloadSaved();
  };

  // ---------------------------------------------------------------- render
  const replay = (h: HistoryEntry) => {
    ocTrack('ocean_history_replay');
    applyFilters({ ...EMPTY_FILTERS, q: h.query || '', countries: h.filters.countries || [], industries: h.filters.industries || [],
                   goals: h.filters.goals || [], tags: h.filters.tags || [] }, true);
  };

  const filtersBody = (
    <FiltersBody meta={meta} facets={facets} applied={applied}
                 onChange={(next, how) => { ocTrack('ocean_filter', how); applyFilters(next); }} />
  );
  const historyProps = {
    history, collections: saved.collections, savedCount: saved.items.length, onReplay: replay,
    onDelete: (id: number) => { setHistory(h => h.filter(x => x.id !== id)); oc.deleteHistory(id); },
    onClear: () => { setHistory([]); oc.clearHistory(); },
    onOpenSaved: (id?: number) => openSavedView(id), onInvite: () => setInviteOpen(true),
  };

  return (
    <OceanContext.Provider value={ctx}>
      <div className="ocx ocx-cosmos">
        {/* Cosmos's search page layout (components/DirectSearchPage.tsx): filters | search + results | history. */}
        <main className="flex flex-1 flex-col self-stretch min-w-0 rounded-2xl bg-white shadow-custom-light overflow-hidden">
          <div className="flex flex-1 min-h-0 overflow-hidden">
            <FiltersPane collapsed={filtersCollapsed} onCollapse={setFiltersCollapsed} onClear={() => applyFilters(EMPTY_FILTERS)}>
              {filtersBody}
            </FiltersPane>
            {filtersDrawer && (
              <FiltersDrawer onClose={() => setFiltersDrawer(false)} onClear={() => applyFilters(EMPTY_FILTERS)}>{filtersBody}</FiltersDrawer>
            )}
            <section className="flex-1 flex flex-col min-w-0">
              <SearchHeader query={query} setQuery={setQuery} searching={searching}
                            onSubmit={() => applyFilters({ ...applied, q: query.trim() }, true)}
                            applied={applied} onChange={next => applyFilters(next)} onClear={() => applyFilters(EMPTY_FILTERS)}
                            historyCount={history.length} onOpenFilters={() => setFiltersDrawer(true)}
                            onOpenHistory={() => setHistoryDrawer(true)} error={error} onRetry={() => runSearch(applied)} />
              <div className="flex-1 overflow-y-auto p-4">
                <ResultsPane applied={applied} searching={searching} search={searchMeta} problems={problems} companies={companies}
                             loadingP={loadingP} loadingC={loadingC} suggestions={suggestions} compact={isMobile}
                             problemsRef={problemsRef} companiesRef={companiesRef} onPage={loadSection}
                             onApply={(f, type) => { ocTrack('ocean_suggestion', { type }); applyFilters(f); }}
                             onAnyway={() => runSearch(appliedRef.current, true, true)} />
              </div>
            </section>
            <HistoryPane {...historyProps} collapsed={historyCollapsed} onCollapse={setHistoryCollapsed} />
          </div>
          {historyDrawer && <HistoryDrawer {...historyProps} onClose={() => setHistoryDrawer(false)} />}
        </main>

        {sharedToken && <SharedCollectionView token={sharedToken} onClose={() => setSharedToken(null)} />}
        {base && (
          <BaseCard r={base} crumbs={crumbs} drawerOpen={!!top} onClose={() => { setBase(null); setStack([]); }}
                    onBackdrop={() => (stack.length ? setStack([]) : setBase(null))} />
        )}
        {top && (
          <Drawer r={top} crumbs={crumbs} backLabel={backLabel}
                  onBack={() => setStack(s => s.slice(0, -1))} onClose={() => setStack([])} />
        )}
        {COLLECTIONS_ENABLED && sheet && (
          <CollectionSheet target={sheetTarget} collections={saved.collections}
                           currentId={sheetTarget ? savedIndex.get(`${sheetTarget.kind}:${sheetTarget.id}`) ?? null : null}
                           onPick={id => pickCollection(id)} onCreate={createCollection} onRename={renameCollection}
                           onDelete={deleteCollection} onClose={() => setSheet(null)}
                           onRemove={sheetTarget ? () => { const t = sheetTarget; setSheet(null); toggleSave(t.kind, t.id); } : undefined} />
        )}
        {inviteOpen && <InviteSheet onClose={() => setInviteOpen(false)} onDone={msg => flash(msg)} />}
        <Toast t={toast} onDismiss={dismissToast} />
      </div>
    </OceanContext.Provider>
  );
};

export default OceanApp;
